import { useEffect, useState } from 'react';

import {
  AddressValidationView,
  USCityView,
  ValidationRequest,
} from '@zola/svc-marketplace-ts-types';
import { composeValidators } from '@zola/zola-ui/src/components/Form/util/composeValidators';

import { FieldValidator } from 'final-form';
import _trim from 'lodash/trim';
import { Field, useForm, useFormState } from 'react-final-form';

import { getStateName } from '~/components/common/form/addresses';
import { NOT_EMPTY_LOCATION } from '~/components/common/form/commonValidations';
import { AutoSuggestLocationFieldAdapter } from '~/components/common/form/FinalForm/Inputs';
import ApiService from '~/util/apiService';

import { FormWithMutators } from '../formWithMutators';
import { CityState, WeddingLocationInputFormType } from './types';

const ADDRESS_VALIDATION_ERROR = 'Please enter a valid city and state, separated by a comma.';

const validateAddress: FieldValidator<CityState> = (
  value: CityState,
  allValues,
  meta
): string | undefined => {
  if (!value.city || !value.stateProvince || !getStateName(value.stateProvince)) {
    return ADDRESS_VALIDATION_ERROR;
  }
  // Only call the endpoint if the user did something
  if (meta?.touched) {
    ApiService.post<AddressValidationView, Partial<ValidationRequest>>(
      '/web-marketplace-api/v2/address/validate',
      value
    )
      .then((data) =>
        data.validationResult === 'NOT_VALID' ? ADDRESS_VALIDATION_ERROR : undefined
      )
      .catch(() => ADDRESS_VALIDATION_ERROR);
  }
  return undefined;
};

export const isAnswered = (values: WeddingLocationInputFormType): boolean => {
  return Boolean(values.weddingLocation?.city && values.weddingLocation.stateProvince);
};

export const WeddingLocationInput = <FormValues extends WeddingLocationInputFormType>({
  placeholder = 'City and state, or metro area',
}) => {
  const { values } = useFormState<FormValues>();
  const form =
    useForm<WeddingLocationInputFormType>() as FormWithMutators<WeddingLocationInputFormType>;
  const { setField } = form.mutators;
  const [weddingLocationString, setWeddingLocationString] = useState('');

  if (typeof setField !== 'function') {
    throw new Error('WeddingLocationInput must be used with a react final form with mutators');
  }

  /**
   * store a string representation of the location in local state,
   * which will be used as the HTML input's value. See handleLocationChange
   * for control of react-final-form field values bound to this.
   */
  useEffect(() => {
    let locationObject: CityState = { city: undefined, stateProvince: undefined };
    if (
      values.weddingLocation &&
      values.weddingLocation.city &&
      values.weddingLocation.stateProvince
    ) {
      locationObject = values.weddingLocation;
      /* update local state with a string representation of the wedding location */
      setWeddingLocationString(
        [locationObject.city, locationObject.stateProvince].filter((item) => item).join(', ')
      );
    }
  }, [values.weddingLocation]);

  /* populate location when autosuggest selected */
  const handleLocationSelected = (
    e: React.SyntheticEvent,
    { suggestion }: { suggestion: USCityView }
  ) => {
    setField('weddingLocation', {
      city: suggestion.name,
      stateProvince: suggestion.stateCode,
    });
  };

  /* populate location when user inputs directly */
  const handleLocationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    payload: { newValue: unknown; method: string }
  ) => {
    const { method } = payload;
    if (method === 'click') {
      return;
    }
    const {
      target: { value = '' },
    } = event;

    const location = value.split(',');
    /* touch the field to trigger validation */
    const fieldState = form.getFieldState('weddingLocation');
    if (!fieldState?.touched) {
      form.mutators.setFieldTouched('weddingLocation', true);
    }
    form.mutators.setField('weddingLocation', {
      city: _trim(location[0]) || null,
      stateProvince: _trim(location[1]) || null,
    });
  };

  return (
    <Field
      component={AutoSuggestLocationFieldAdapter}
      name="weddingLocation"
      useLabel={false}
      labelFor="weddingLocation"
      input={{
        name: 'weddingLocation',
        value: weddingLocationString,
        inputV2: true,
        placeholder,
      }}
      // Tries to set a city/state from the user's input (not using the suggestion)
      onInputChange={handleLocationChange}
      // Sets city/state from a suggestion
      onSuggestionSelected={handleLocationSelected}
      validate={composeValidators(NOT_EMPTY_LOCATION, validateAddress)}
      // https://final-form.org/docs/react-final-form/types/FieldProps#validatefields
      // An array of field names to validate when this field changes. If
      // undefined, every field will be validated when this one changes;
      // if [], only this field will have its field-level validation function
      // called when it changes
      validateFields={[]}
    />
  );
};
